import { useEffect } from 'react';
import bootstrap from 'bootstrap/dist/js/bootstrap.min.js';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { Route, Routes, useNavigate } from 'react-router';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import { Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { AdminUserProvider } from './contexts/user-context';
import RequireAuth from './services/require-auth';
import config from './auth-config';

import { LicenseInfo } from '@mui/x-license';

import ReactGA from "react-ga4";
import { useLocation } from 'react-router';

import Layout from './components/layout/layout';
import ScrollToTop from './components/scroll-to-top';
import './t1bootstrap.scss'
import './app.scss'

import { lazyLoadWithRetry } from './services/lazy-load-with-retry';

import SpinnerLoader from './components/layout/spinner-loader';

import Home from './components/home/home';

import BucksOneBalances from './components/tools/bucksone-balances';
import BuilderAssessment from './components/tools/builder-assessment';
import Classes from './components/tools/classes';
import CommunityEvents from './components/tools/community-events';
import CourtesySigningRequest from './components/tools/courtesy-signing-request';
import DigitalHomePortfolios from './components/tools/digital-home-portfolios';
import Ecards from './components/tools/ecards/ecards';
import ForeclosureSales from './components/tools/foreclosure-sales';
import HomePageCards from './components/tools/home-page-cards';
import Impersonate from './components/impersonate';
import ItemOfTheWeek from './components/tools/item-of-the-week';
import JobPosting from './components/tools/job-postings';
import ReconveyanceManagement from './components/tools/reconveyance-management';
import LoggedOut from './components/home/loggedout';
import MarketShare from './components/tools/market-share';
import MerchOneHeader from './components/tools/merchone/merchone-header';
import MerchOne from './components/tools/merchone/merchone';
import MerchOneCheckout from './components/tools/merchone/merchone-checkout';
import MerchOneManagement from './components/tools/merchone/merchone-management';
import MerchOneProducts from './components/tools/merchone/merchone-products';
import NewsArticles from './components/tools/news-articles';
import Offices from './components/tools/offices';
import PreferredCustomers from './components/tools/preferred-customers';
import PromoItemManagement from './components/tools/promo-item-management';
import PromoOrderManagement from './components/tools/promo-order-management';
import RepsOfTheYear from './components/tools/reps-of-the-year';
import SalesToolkit from './components/tools/sales-toolkit';
import SiteMessages from './components/tools/site-messages';
import TeamMembers from './components/tools/team-members';
import PromoOrderZones from './components/tools/promo-order-zones';
import PromoOrderForm from './components/tools/promo-order-form';
import TextAPickUp from './components/tools/text-a-pick-up';
import LegalDescriptionExtractor from './components/tools/legal-description-extractor';
import TpmAnalytics from './components/tools/tpm-analytics';
import TpmDashboard from './components/tools/tpm-dashboard';
import { Suspense } from 'react';

const Oops = lazyLoadWithRetry(() => import('./components/oops'));
const PageNotFound = lazyLoadWithRetry(() => import('./components/page-not-found'));

const oktaAuth = new OktaAuth(config.oidc);


const theme = createTheme({
    palette: {
        primary: {
            main: '#8A7340'
        }
    },
    components: {
        MuiPopover: {
            defaultProps: {
                disableScrollLock: true,
            },
        },

        MuiChip: {
            variants: [
                {
                    props: { size: 'large' },
                    style: {
                        height: 35,
                        fontSize: '.9rem',
                        paddingInline: 8,
                        borderRadius: 18,
                        '& .MuiChip-icon': {
                            fontSize: 20,
                        },
                        '& .MuiChip-deleteIcon': {
                            fontSize: 20,
                        },
                    },
                },
            ],
        },
    },
});

const App = () => {
    
    const navigate = useNavigate();
    const restoreOriginalUri = (_oktaAuth, originalUri) => {
        navigate(toRelativeUrl(originalUri || '/', window.location.origin));
    };

    LicenseInfo.setLicenseKey(process.env.REACT_APP_MUIX_LICENSE_KEY);

    const location = useLocation();
    useEffect(() => {

        ReactGA.initialize(process.env.REACT_APP_GACODE);
        ReactGA.send({ hitType: "pageview", page: location.pathname });

    }, [location]);

    useEffect(() => {
        window.onpopstate = e => {
            document.querySelectorAll(".modal-backdrop").forEach(e => e.remove());
            document.querySelector("body").classList.remove("modal-open");
            document.querySelector("body").removeAttribute("style");
        };

    }, []);

    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
                <AdminUserProvider>
                    <ScrollToTop>
                        <Routes>
                            <Route path='/' element={<Layout />} >
                                <Route path='/loggedout' element={<LoggedOut />} />
                                <Route path='/clothing-store-management' element={<MerchOneManagement />} />
                                <Route path='/merchone-management' element={<MerchOneManagement />} />
                                <Route path="/oops" element={<Suspense fallback={<SpinnerLoader/>}><Oops /></Suspense>} />
                            </Route>
                            <Route path='/login/callback' element={<LoginCallback loadingElement={<SpinnerLoader />} />} />

                            <Route element={<RequireAuth><Layout /></RequireAuth>}>
                                <Route path="*" element={<Suspense fallback={<SpinnerLoader/>}><PageNotFound /></Suspense>} />
                                <Route index element={<Home />} />
                                <Route path='/bucksone-balances' element={<BucksOneBalances />} />
                                <Route path='/builder-assessment' element={<BuilderAssessment />} />
                                    <Route path='/builder-assessment/:paramAssessment' element={<BuilderAssessment />} />
                                <Route path='/classes' element={<Classes brandId={1} />} />
                                <Route path='/classes-real1031' element={<Classes brandId={3} />} />
                                <Route element={<MerchOneHeader />}>
                                    <Route path="/clothing-store" element={<MerchOne />} />
                                    <Route path="/merchone" element={<MerchOne />} />
                                    <Route path="/merchone/:paramMerchOneItemId" element={<MerchOne />} />
                                    <Route path="/merchone-checkout" element={<MerchOneCheckout />} />
                                </Route>
                                <Route path='/merchone-products' element={<MerchOneProducts />} />
                                <Route path='/community-events' element={<CommunityEvents />} />
                                <Route path='/courtesy-signing-request' element={<CourtesySigningRequest />} />
                                <Route path='/digital-home-portfolios' element={<DigitalHomePortfolios />} />
                                <Route path='/ecards' element={<Ecards />} />
                                <Route path='/foreclosure-sales' element={<ForeclosureSales />} />
                                <Route path='/home-page-cards' element={<HomePageCards />} />
                                <Route path='/impersonate' element={<Impersonate />} />
                                <Route path='/item-of-the-week' element={<ItemOfTheWeek />} />
                                <Route path='/job-postings' element={<JobPosting />} />
                                <Route path='/reconveyance-management' element={<ReconveyanceManagement />} />
                                <Route path='/market-share' element={<MarketShare />} />
                                <Route path='/news-articles' element={<NewsArticles />} />
                                <Route path='/offices' element={<Offices />} />
                                <Route path='/preferred-customers' element={<PreferredCustomers />}>
                                    <Route path='/preferred-customers/:paramPreferredCustomer' element={<PreferredCustomers />} />
                                </Route>
                                <Route path='/promo-item-management' element={<PromoItemManagement />} />
                                <Route path='/promo-order-form' element={<PromoOrderForm />}>
                                    <Route path='/promo-order-form/:paramPromoZone' element={<PromoOrderForm />} />
                                </Route>
                                <Route path='/promo-order-management' element={<PromoOrderManagement />} />
                                <Route path='/promo-order-zones' element={<PromoOrderZones />} />
                                <Route path='/reps-of-the-year' element={<RepsOfTheYear />} />
                                <Route path='/sales-toolkit' element={<SalesToolkit />} />
                                <Route path='/site-messages' element={<SiteMessages />} />
                                <Route path='/team-members' element={<TeamMembers />} />
                                <Route path='/text-a-pick-up' element={<TextAPickUp />} />
                                <Route path='/tpm-analytics' element={<TpmAnalytics />} />
                                <Route path='/tpm-dashboard' element={<TpmDashboard />} />
                                <Route path='/legal-description-extractor' element={<LegalDescriptionExtractor />}>
                                    <Route path='/legal-description-extractor/:paramRwFileNumber' element={<LegalDescriptionExtractor />} />
                                </Route>
                            </Route>
                        </Routes>
                    </ScrollToTop>
                </AdminUserProvider>
            </Security>
            </LocalizationProvider>
        </ThemeProvider>

    );
}

export { App, bootstrap };
